import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, MousePointerClick, UserPlus, DollarSign, TrendingUp, Undo2 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SignInRequired } from "@/components/SignInRequired";
import { AffiliateCodeAnalytics } from "@/components/AffiliateCodeAnalytics";

export const Route = createFileRoute("/affiliate/analytics")({
  component: AffiliateAnalyticsPage,
  head: () => ({
    meta: [
      { title: "Referral analytics - Inquira Creator Program" },
      { name: "description", content: "Track clicks, signups, conversions, and commission earned or reversed across any date range." },
      { property: "og:title", content: "Inquira referral analytics" },
      { property: "og:description", content: "Clicks, signups, conversions, and commissions for Inquira creators." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Analytics = {
  ok: boolean;
  affiliate: { id: string; status: string; affiliate_code: string | null; commission_pct: number | null } | null;
  range?: { from: string; to: string };
  totals?: {
    clicks: number; unique_visitors: number; signups: number; trials: number; conversions: number;
    earned_cents: number; reversed_cents: number; revenue_cents: number;
  };
  series?: Array<{ day: string; clicks: number; signups: number; conversions: number; earned_cents: number; reversed_cents: number }>;
  sources?: Array<{ source: string; clicks: number }>;
  commissions?: Array<{ id: string; billing_platform: string; commission_amount: number; eligible_net_amount: number; status: string; created_at: string }>;
};

const money = (c: number) => `$${((c ?? 0) / 100).toFixed(2)}`;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

function AffiliateAnalyticsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(isoDay(new Date()));

  const load = useCallback(async (fromDay: string, toDay: string) => {
    setLoading(true);
    const { data: res, error } = await supabase.rpc("my_affiliate_analytics" as never, {
      _from: new Date(`${fromDay}T00:00:00`).toISOString(),
      _to: new Date(`${toDay}T23:59:59`).toISOString(),
    } as never);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setData(res as unknown as Analytics);
  }, []);

  useEffect(() => {
    if (!user) return;
    void load(from, to);
  }, [user, from, to, load]);

  const chart = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        day: new Date(s.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        clicks: s.clicks,
        signups: s.signups,
        conversions: s.conversions,
        earned: Number(((s.earned_cents ?? 0) / 100).toFixed(2)),
        reversed: Number(((s.reversed_cents ?? 0) / 100).toFixed(2)),
      })),
    [data],
  );

  if (!authLoading && !user) return <SignInRequired title="Sign in to view your referral analytics" />;

  const t = data?.totals;
  const clicks = t?.clicks ?? 0;
  const conv = t?.conversions ?? 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/affiliate" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {[
            { label: "7d", days: 7 },
            { label: "30d", days: 30 },
            { label: "90d", days: 90 },
          ].map((r) => (
            <button
              key={r.label}
              onClick={() => {
                setFrom(isoDay(new Date(Date.now() - (r.days - 1) * 86_400_000)));
                setTo(isoDay(new Date()));
              }}
              className="rounded-full border border-border px-3 py-1.5"
            >
              {r.label}
            </button>
          ))}
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">From</span>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="rounded-lg border border-border bg-background p-1.5" />
          </label>
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">To</span>
            <input type="date" value={to} min={from} max={isoDay(new Date())} onChange={(e) => setTo(e.target.value)} className="rounded-lg border border-border bg-background p-1.5" />
          </label>
        </div>
      </div>

      <h1 className="text-2xl font-bold">Referral &amp; commission analytics</h1>
      <p className="mb-5 text-sm text-muted-foreground">
        {loading ? "Loading…" : `Clicks, signups, conversions and commissions from ${from} to ${to}.`}
      </p>

      {data && !data.affiliate ? (
        <div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">
          You’re not part of the creator program yet.{" "}
          <Link to="/affiliates" className="text-primary underline">
            Apply here
          </Link>
          .
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: "Clicks", value: String(clicks), icon: MousePointerClick },
              { label: "Unique visitors", value: String(t?.unique_visitors ?? 0), icon: MousePointerClick },
              { label: "Signups", value: String(t?.signups ?? 0), icon: UserPlus },
              { label: "Conversions", value: String(conv), icon: TrendingUp },
              { label: "Commission earned", value: money(t?.earned_cents ?? 0), icon: DollarSign },
              { label: "Reversed", value: money(t?.reversed_cents ?? 0), icon: Undo2 },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-border p-4">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <s.icon className="h-3 w-3" /> {s.label}
                </div>
                <div className="mt-1 text-lg font-bold">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border p-4">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold">Funnel over time</h2>
              <span className="text-xs text-muted-foreground">
                Click → signup {clicks ? `${((100 * (t?.signups ?? 0)) / clicks).toFixed(1)}%` : "—"} · signup → paid{" "}
                {(t?.signups ?? 0) ? `${((100 * conv) / (t!.signups || 1)).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.2)" />
                  <Area type="monotone" dataKey="signups" stroke="hsl(var(--accent))" fill="hsl(var(--accent) / 0.2)" />
                  <Area type="monotone" dataKey="conversions" stroke="hsl(var(--neon-pink, var(--primary)))" fill="hsl(var(--primary) / 0.1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border p-4">
              <h2 className="mb-2 text-lg font-semibold">Commission earned vs reversed</h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="earned" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="reversed" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-border p-4">
              <h2 className="mb-2 text-lg font-semibold">Top traffic sources</h2>
              {(data?.sources?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No clicks recorded in this range.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {data!.sources!.map((s) => (
                    <li key={s.source} className="flex justify-between py-2">
                      <span className="capitalize">{s.source}</span>
                      <span className="font-medium">{s.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border p-4">
            <h2 className="mb-2 text-lg font-semibold">Commissions in range</h2>
            {(data?.commissions?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No commissions recorded in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3 text-right">Revenue</th>
                      <th className="py-2 pr-3 text-right">Commission</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data!.commissions!.map((c) => (
                      <tr key={c.id}>
                        <td className="py-2 pr-3">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="py-2 pr-3 capitalize">{c.billing_platform}</td>
                        <td className="py-2 pr-3 text-right">${Number(c.eligible_net_amount ?? 0).toFixed(2)}</td>
                        <td className="py-2 pr-3 text-right font-medium">${Number(c.commission_amount ?? 0).toFixed(2)}</td>
                        <td className="py-2 capitalize">{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <AffiliateCodeAnalytics from={from} to={to} />
        </div>
      )}
    </div>
  );
}
